import ModalDialogConfirm from '@manager/modules/modals/variants/ModalDialogConfirm.vue';
import type { ITableRowConfirmData } from '@manager/types/action-menu-types';
import { uniqueId } from 'lodash-es';
import { markRaw, reactive, type Ref, ref } from 'vue';

type PropsType = Record<string, unknown>;

export interface ModalInstance<
  Component = unknown,
  Props extends PropsType = PropsType,
  Return = unknown,
> {
  id: string;
  resolve: (value: Return) => void;
  afterClose: Promise<Return>;
  component: Component;
  props: Props;
}

function createInstance<T, Props extends PropsType, Return>(
  component: T,
  props: Props
): ModalInstance<T, Props, Return> {
  let resolve: (type: Return) => void = () => undefined;

  const afterClose = new Promise<Return>((resolveFn) => {
    resolve = resolveFn;
  });

  return {
    id: uniqueId('modal-'),
    resolve,
    afterClose,
    component,
    props,
  };
}

export interface ConfirmConfigurableProps extends PropsType {
  okLabel?: ITableRowConfirmData['okLabel'];
  cancelLabel?: ITableRowConfirmData['cancelLabel'];
  title?: ITableRowConfirmData['title'];
  icon?: ITableRowConfirmData['icon'];
  message?: ITableRowConfirmData['message'];
  color?: ITableRowConfirmData['color'];
  loading?: boolean;
  width?: 'sm' | 'md' | 'lg' | 'xl' | 'unset';
  /**
   * Optional callback that will be executed when the user confirms (clicks OK).
   * If the callback returns a Promise, the dialog will stay open and display
   * the loading state until the promise resolves (or rejects).
   */
  onConfirm?: () => void | Promise<void>;
  resolve?: (value: boolean) => void;
  dataTestId?: string;
}

export interface ConfirmProps extends ConfirmConfigurableProps {
  modelValue: boolean;
  onClosed?: (value: boolean) => void;
  onAfterClose?: () => void;
}

export type TAnyModal = ModalInstance<typeof ModalDialogConfirm, ConfirmProps>;

const queue: Ref<TAnyModal[]> = ref([]);

export interface UseModals {
  confirm: (
    opt: ConfirmConfigurableProps
  ) => ModalInstance<typeof ModalDialogConfirm, ConfirmProps>;
}
export function useModalQueue(): {
  queue: typeof queue;
  push(item: TAnyModal): void;
} {
  return {
    queue,
    push(item: TAnyModal) {
      queue.value.push(markRaw(item));
    },
  };
}

export function useModals(): UseModals {
  const { push, queue } = useModalQueue();

  return {
    confirm(opt: ConfirmConfigurableProps) {
      let result: boolean | undefined = undefined;

      // Create the reactive props object, including event handlers
      const modalProps = reactive<ConfirmProps>({
        ...opt,
        modelValue: true,
        // This handler is called when ModalDialogConfirm emits 'closed' (user clicked OK/Cancel)
        onClosed: async (value: boolean) => {
          result = value;

          // If the user confirmed and an onConfirm handler was provided, execute it
          if (value && typeof opt.onConfirm === 'function') {
            try {
              modalProps.loading = true;
              // Wait for the (possible) promise to settle
              await opt.onConfirm();
            } finally {
              modalProps.loading = false;
            }
          }

          // Close the modal after the optional onConfirm handler finished
          modalProps.modelValue = false;
        },
        // This handler is called when ModalDialogConfirm emits 'after-close' (animation finished)
        onAfterClose: () => {
          queue.value = queue.value.filter((modal) => modal.id !== instance.id);
          instance.resolve(result ?? false); // Resolve with stored result (or default)
        },
      });

      const instance = createInstance(ModalDialogConfirm, modalProps);

      push(instance);
      return instance;
    },
  };
}
